import { Fade, Stack } from '@mui/material';
import Typography from '@mui/material/Typography';
import useColorsStyles from 'common/colors';
import useFlexBoxStyles from 'common/flexBox';
import useOnliveStyles from 'common/onliveStyles';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => {
    return {
        root: {
            width: theme.spacing(10),
            minWidth: theme.spacing(10),
            minHeight: theme.spacing(13),
        },
        content: {
            borderRadius: theme.custom.borderRadius,
            boxShadow: theme.custom.boxShadowWeb,
        },

        sizeBig: {
            width: '96px',
            minWidth: '96px',
            height: theme.spacing(16),
            [theme.breakpoints.up('xl')]: {
                width: '121px',
                minWidth: '121px',
                height: theme.spacing(18),
            },
        },

        top: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '4px 0',
            [theme.breakpoints.up('xl')]: {
                padding: '6px 0',
            },
        },

        endsInText: {
            color: 'white',
            fontSize: '8px',
            fontWeight: '600',
            [theme.breakpoints.up('xl')]: {
                fontSize: '10px',
            },
            textTransform: 'uppercase',
        },

        nameContainer: {
            background: theme.palette.secondary.main,
            textAlign: 'center',
            padding: '3px 0',
            [theme.breakpoints.up('xl')]: {
                padding: '5px 0',
            },
        },
        nameContainerSmall: {
            padding: '1px 0',
        },
        nameText: {
            color: theme.palette.primary.main,
            fontSize: '15px',
            fontWeight: '900',
            [theme.breakpoints.up('xl')]: {
                fontSize: '19px',
            },
            textTransform: 'uppercase',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
        },

        nameSmall2Lines: {
            fontSize: '11px',
            fontWeight: '900',
            [theme.breakpoints.up('xl')]: {
                fontSize: '14px',
            },
        },

        nameSmall2LinesBig: {
            fontSize: '14px',
        },
        percentContainer: {
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        percent: {
            color: '#F2D507',
            fontSize: '32px',
            fontWeight: '800',
            lineHeight: '40px',
            [theme.breakpoints.up('xl')]: {
                fontSize: '41px',
                lineHeight: '50px',
            },
        },
        percentBig: {
            fontSize: '41px',
            lineHeight: '50px',
        },

        percentSymbol: {
            color: '#F2D507',
            fontSize: '16px',
            lineHeight: '20px',
            fontWeight: '800',
            [theme.breakpoints.up('xl')]: {
                fontSize: '21px',
                lineHeight: '25px',
            },
        },

        percentSymbolBig: {
            fontSize: '21px',
            lineHeight: '25px',
        },
        percentOFF: {
            color: '#F2D507',
            fontSize: '8px',
            lineHeight: '9px',
            fontWeight: '800',
            [theme.breakpoints.up('xl')]: {
                fontSize: '10px',
                lineHeight: '13px',
            },
        },

        percentOFFBig: {
            fontSize: '10px',
            lineHeight: '13px',
        },
        description: {
            textAlign: 'center',
            color: theme.palette.secondary.main,
            fontSize: '8px',
            fontWeight: '500',
            [theme.breakpoints.up('xl')]: {
                fontSize: '10px',
            },
            textTransform: 'uppercase',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
        },

        descriptionBig: {
            fontSize: '10px',
        },

        '@keyframes glow': {
            '0%': {
                opacity: 1,
                textShadow: '0 0 3px rgba(255, 255, 255, 0.3), 0 0 3px #ff3f40',
            },
            '100%': {
                opacity: 1,
                textShadow:
                    '0 0 5px rgba(255, 255, 255, 0.3), 0 0 10px rgba(255, 255, 255, 0.3), 0 0 8px #ff3f40, 0 0 12px #ff3f40',
            },
        },

        '@keyframes shake': {
            '10%, 90%': {
                transform: 'translate3d(-1px, 0, 0)',
            },

            '20%, 80%': {
                transform: 'translate3d(2px, 0, 0)',
            },

            '30%, 50%, 70%': {
                transform: 'translate3d(-4px, 0, 0)',
            },

            '40%, 60%': {
                transform: 'translate3d(4px, 0, 0)',
            },
        },
        withShake: {
            animation: '$shake 1s ease-in-out infinite alternate',
        },
        withGlow: {
            animation: '$glow 1s ease-in-out infinite alternate',
        },
    };
});

const renderPercent = (percent) => {
    return String(Math.floor(percent));
};

const DiscountCard = (props) => {
    const { asset } = props;

    const { classes, cx } = useStyles();
    const { classes: flexBox } = useFlexBoxStyles();
    const { classes: colors } = useColorsStyles();
    const { classes: onlive } = useOnliveStyles();

    const { t } = useTranslation();

    const [timeString, setTimeString] = useState('00:00');

    useEffect(() => {
        let date = new Date(asset.duration * 1000).toISOString().substring(11, 16);
        setTimeString(date);
    }, [asset]);

    let nameClass = classes.nameText;
    let percentClass = classes.percent;
    let percentSymbolClass = classes.percentSymbol;
    let offClass = classes.percentOFF;
    let descriptionClass = classes.description;
    let nameContainerClass = classes.nameContainer;
    let topClass = classes.top;
    let endsInClass = classes.endsInText;

    let rootClass = cx(
        flexBox.borderRadius2,
        flexBox.opacity1,
        colors.bgPrimary,
        flexBox.relative,
        flexBox.overflowHidden,
        flexBox.h100,
        flexBox.borderBox,
        classes.root,
        classes.percentBig,
        classes.percentSymbolBig,
        classes.percentOFFBig,
        classes.descriptionBig,
        classes.sizeBig,
        classes.nameSmall2LinesBig,
    );
    return (
        <Stack className={cx(onlive.boxShadowWeb, flexBox.borderRadius2)}>
            <Fade in={true}>
                <div className={rootClass}>
                    <div className={topClass}>
                        <Typography className={endsInClass}>
                            {t('web.conference.discountCard.endsIn')}:
                            <span className={endsInClass}>
                                <b>{timeString}</b>
                            </span>
                        </Typography>
                    </div>
                    <div className={nameContainerClass}>
                        <Typography className={nameClass}>{asset.name}</Typography>
                    </div>
                    <div className={classes.percentContainer}>
                        <Typography className={percentClass}>{renderPercent(asset.percent)}</Typography>
                        <div>
                            <Typography className={percentSymbolClass}>%</Typography>
                            <Typography className={offClass}>OFF</Typography>
                        </div>
                    </div>
                    {Boolean(asset.description) && (
                        <Typography className={descriptionClass}>{asset.description}</Typography>
                    )}
                </div>
            </Fade>
        </Stack>
    );
};

export default DiscountCard;
